import { useRef, useEffect } from "react";
import { motion, useInView, useSpring, useTransform, useScroll } from "framer-motion";

export const EASE = [0.22, 1, 0.36, 1];

// Section scroll reveal (fade + rise)
export const Reveal = ({ children, delay = 0, y = 40, className = "", as: Tag = motion.div }) => (
  <Tag
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, ease: EASE, delay }}
  >
    {children}
  </Tag>
);

// Masked line-by-line kinetic reveal for hero headings
export const KineticLines = ({ lines, className = "", lineClass = "", stagger = 0.12, delay = 0.1 }) => (
  <span className={className}>
    {lines.map((line, i) => (
      <span key={i} className="block overflow-hidden">
        <motion.span
          className={`block ${lineClass}`}
          initial={{ y: "110%" }}
          animate={{ y: "0%" }}
          transition={{ duration: 1.1, ease: EASE, delay: delay + i * stagger }}
        >
          {line}
        </motion.span>
      </span>
    ))}
  </span>
);

// Word-by-word reveal on scroll
export const WordsReveal = ({ text, className = "", wordClass = "" }) => {
  const words = text.split(" ");
  return (
    <span className={className}>
      {words.map((w, i) => (
        <span key={i} className="inline-block overflow-hidden align-bottom">
          <motion.span
            className={`inline-block ${wordClass}`}
            initial={{ y: "110%" }}
            whileInView={{ y: "0%" }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: EASE, delay: i * 0.03 }}
          >
            {w}&nbsp;
          </motion.span>
        </span>
      ))}
    </span>
  );
};

// Animated counter
export const Counter = ({ to, suffix = "", duration = 2, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const spring = useSpring(0, { duration: duration * 1000, bounce: 0 });
  const display = useTransform(spring, (v) => Math.floor(v).toLocaleString());
  useEffect(() => {
    if (inView) spring.set(to);
  }, [inView, to, spring]);
  return (
    <span ref={ref} className={className}>
      <motion.span>{display}</motion.span>
      {suffix}
    </span>
  );
};

// Parallax wrapper based on scroll
export const Parallax = ({ children, offset = 80, className = "" }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [offset, -offset]);
  return (
    <motion.div ref={ref} style={{ y }} className={`relative ${className}`}>
      {children}
    </motion.div>
  );
};

// Section eyebrow label with gold tick
export const Eyebrow = ({ children, dark = false }) => (
  <div className="flex items-center gap-3 mb-6">
    <span className="h-px w-10 bg-gold" />
    <span className={`font-accent text-xs tracking-[0.28em] uppercase ${dark ? "text-white/60" : "text-ink/50"}`}>
      {children}
    </span>
  </div>
);

// Primary gold button
export const GoldButton = ({ children, onClick, className = "", ...props }) => (
  <motion.button
    onClick={onClick}
    whileHover={{ scale: 1.03 }}
    whileTap={{ scale: 0.98 }}
    transition={{ duration: 0.3, ease: EASE }}
    className={`group relative inline-flex items-center gap-2 rounded-full bg-gold px-8 py-3.5 font-accent text-sm tracking-wide text-ink font-medium overflow-hidden ${className}`}
    {...props}
  >
    <span className="relative z-10 flex items-center gap-2">{children}</span>
    <span className="absolute inset-0 bg-gold-light translate-y-full group-hover:translate-y-0 transition-transform duration-400 ease-out" />
  </motion.button>
);

// Outline button
export const OutlineButton = ({ children, onClick, dark = false, className = "", ...props }) => (
  <motion.button
    onClick={onClick}
    whileHover={{ scale: 1.03 }}
    whileTap={{ scale: 0.98 }}
    transition={{ duration: 0.3, ease: EASE }}
    className={`inline-flex items-center gap-2 rounded-full border px-8 py-3.5 font-accent text-sm tracking-wide transition-colors duration-300 ${
      dark
        ? "border-white/25 text-white hover:border-gold hover:text-gold"
        : "border-ink/20 text-ink hover:border-gold hover:text-gold"
    } ${className}`}
    {...props}
  >
    {children}
  </motion.button>
);
